/**
@format
@flow
*/

import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import ActionTypes from '@ziynx/action-types';
import { eventNames } from '@ziynx/analytics';
import Colors from '@ziynx/theme/colors';
import { ContentContainer } from '@ziynx/components/layout';
import { withAppManager, useNotification } from '@ziynx/components/utilities';
import { Button } from '@ziynx/components/buttons';
import Link from '@ziynx/components/links';
import Headline from '@ziynx/components/headlines';
import { InputAutocomplete } from '@ziynx/components/inputs';
import { Form } from '@ziynx/components/forms';
import type { ManagedProps } from '@ziynx/flow-types/appManager';

type History = {
  push: (pathName: string, params?: {
    title?: string,
  }) => void,
  action?: string,
};

type Location = {
  state: {
    title?: string,
    backLink?: string,
  },
};

type DispatchAction = {
  type: string,
} => void;

type Props = $ReadOnly<{||}>;

const PageWrapper = styled.div`
  padding: ${({ isMobile }) => (isMobile ? '40px 16px 200px 16px' : '40px 40px 200px 40px')};
  width: 100%;
  max-width: 688px;
  box-sizing: border-box;
  ::-webkit-scrollbar-track, ::-webkit-scrollbar-thumb, ::-webkit-scrollbar  {
    background-color: transparent;
  }
  ::-webkit-scrollbar {
    display: none;
  }
`;

const LinkWrapper = styled.div`
  padding-bottom: 24px;
`;

const HeadlineWrapper = styled.div`
  padding-bottom: 40px;
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: ${({ isNonDesktop }) => (isNonDesktop ? 'column' : 'row')};
  padding-bottom: 40px;
`;

const StyledButton = styled(Button)`
  ${({ lastChild, isMobile, isMobileSmall }) => !lastChild && `margin-${!isMobile && isMobileSmall ? 'bottom' : 'right'}: 16px;`}
`;

const InputWrapper = styled.div`
  padding-bottom: ${({ lastChild }) => (lastChild ? '40px' : '32px')};
`;

const GRAD_SEASONS = [
  {
    text: 'Spring',
  },
  {
    text: 'Fall',
  },
  {
    text: 'Summer',
  },
  {
    text: 'Winter',
  },
];

const FilterScreen = (props: ManagedProps<Props>): React$Node => {
  const { viewport } = props;

  const dispatch: DispatchAction = useDispatch();
  const { notificationDispatch } = useNotification();
  const history: History = useHistory();
  const location: Location = useLocation();

  const filterService = useSelector(({ filter }) => {
    if (Object.keys(filter?.inProgress ?? {}).length) {
      return filter?.inProgress ?? {};
    }
    return filter?.appliedFilters ?? {};
  }, shallowEqual);

  const discoverService = useSelector(
    ({ discover }) => discover[ActionTypes.discover.postFilters.GROUP] || {}, shallowEqual,
  );

  const searchService = useSelector(({ search }) => ({
    graduationYear: search[ActionTypes.search.getGraduationYears.GROUP] || {},
    location: search[ActionTypes.search.getLocation.GROUP] || {},
    interests: search[ActionTypes.search.getInterests.GROUP] || {},
    collegeOrLeague: search[ActionTypes.search.getCollegeLeague.GROUP] || {},
  }), shallowEqual);

  /** RESET STATE FOR POST_DISCOVER_FILTERS_API */
  const clearPostFilters = () => {
    dispatch({ type: ActionTypes.discover.clearPostFilters });
  };

  const goBack = () => {
    history.push(
      location?.state?.backLink || '/discover',
      { title: location?.state?.title, from: 'Filter' },
    );
  };

  const formatLocations = (locations) => (locations || []).map((item) => ({
    _id: item?.place_id ?? item?._id,
    text: item?.name,
  }));

  const formatInterests = (interests) => (interests || []).map((item) => ({
    _id: item?._id,
    text: item?.name,
  }));

  const formatCollegeLeague = (collegeOrLeague) => (collegeOrLeague || []).map((item) => ({
    _id: item?._id,
    text: item?.name, // Normalized to name from "value" already at this point
  }));

  const [
    selectedLocations, setLocations,
  ] = useState(formatLocations(filterService?.location || []));

  const [
    selectedInterests, setInterests,
  ] = useState(formatInterests(filterService?.interests) || []);

  const [
    selectedCollegeLeagues, setCollegeLeagues,
  ] = useState(formatCollegeLeague(filterService?.collegeOrLeague) || []);

  // FOR SEASON FILTERS
  const [seasonText, setSeasonText] = useState('');
  const [seasonOptions, setSeasonOptions] = useState(GRAD_SEASONS);

  useEffect(() => {
    if (!filterService?.fetching && filterService?.location) {
      setLocations(formatLocations(filterService?.location));
    }
  }, [filterService?.location]);

  /**
     * CHECK IF USER HITS BROWSEER BACK BUTTON
     * TO CLEAR IN PROGRESS FILTERS IF NOT SAVED
     * @see https://stackoverflow.com/a/61961026
     */
  useEffect(() => () => {
    if (history?.action === 'POP') {
      dispatch({
        type: ActionTypes.filters.removeInProgress,
      });
    }
  }, []);

  useEffect(() => () => {
    dispatch({
      type: ActionTypes.analytics.logEvent.START,
      name: eventNames.navigated_filter,
    });
  }, []);

  useEffect(() => {
    if (discoverService?.success && !discoverService?.fetching) {
      goBack();
      clearPostFilters();
    }
    if (!discoverService?.fetching
      && !discoverService?.success
      && discoverService?.error) {
      notificationDispatch({
        type: ActionTypes.notifications.add.START,
        payload: {
          variant: 'error',
          content: 'Something went wrong, please try again later.',
        },
      });
      setTimeout(() => clearPostFilters(), 6000);
    }
  }, [discoverService?.success,
    discoverService?.fetching,
    discoverService?.error]);

  const isMobile = viewport === 'mobile' || viewport === 'mobileSmall';

  const searchActionDispatch = (extraDispatch, field: string) => {
    let type: string = '';
    switch (field) {
    case 'location':
      type = ActionTypes.search.getLocation.START;
      break;
    case 'graduationYear':
      type = ActionTypes.search.getGraduationYears.START;
      break;
    case 'interests':
      type = ActionTypes.search.getInterests.START;
      break;
    case 'collegeOrLeague':
      type = ActionTypes.search.getCollegeLeague.START;
      break;
    default:
      break;
    }
    dispatch({
      type,
      ...extraDispatch,
    });
  };

  const onSearch = (field, value) => {
    const query = {
      name: value,
      page: 0,
    };

    searchActionDispatch({ query }, field);
  };

  /** USED FOR SELECTED AND OPTIONS ARRAY */
  const getArrayItems = (valueArray: Array<any>, selected = []) => (
    valueArray || []).filter((i) => !selected.some((j) => j._id === i._id)).map((item) => (
    {
      ...!!item?._id && { _id: item?._id },
      // eslint-disable-next-line no-nested-ternary
      text: item?.name ? item?.name
        : (item?.value ? item?.value : item), // Use text because data structure of buttonPillGroup
    }));

  /** TO FILTER OPTIONS ARRAY WHEN AN OPTION IS ALREADY CHOSEN AS FILTER */
  const removeDuplicated = (valueArray: Array<{ text: string }>,
    fieldId: string) => valueArray.filter(
    (item) => !(filterService?.[fieldId] || []).some(
      (filter) => (filter?.name ? filter?.name === item?.text : filter === item?.text),
    ),
  );

  const locationsScroll = (e: SyntheticEvent<HTMLUListElement>, { hasReachedEnd }) => {
    if (searchService?.location?.isLastPage) return;
    if (hasReachedEnd) {
      dispatch({
        type: ActionTypes.search.getLocation.START,
        query: { page: searchService?.location?.page + 1 },
      });
    }
  };

  const handleLocationsFocus = () => {
    onSearch('location', '');
  };

  const interestsScroll = (e, { hasReachedEnd }) => {
    if (searchService?.interests?.isLastPage) return;
    if (hasReachedEnd) {
      dispatch({
        type: ActionTypes.search.getInterests.START,
        query: { page: searchService?.interests?.page + 1 },
      });
    }
  };

  const handleInterestsFocus = () => {
    onSearch('interests', '');
  };

  const collegeOrLeagueScroll = (e, { hasReachedEnd }) => {
    if (searchService?.collegeOrLeague?.isLastPage) return;
    if (hasReachedEnd) {
      dispatch({
        type: ActionTypes.search.getCollegeLeague.START,
        query: { page: searchService?.collegeOrLeague?.page + 1 },
      });
    }
  };

  const handleCollegeLeague = () => {
    onSearch('collegeOrLeague', '');
  };

  return (
    <>
      <Helmet>
        <title>Filter | ZIYNX</title>
      </Helmet>
      <ContentContainer offsetTop={isMobile ? 86 : 96}>
        <PageWrapper isMobile={isMobile}>
          <LinkWrapper>
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <Link
              variant="caret"
              onClick={() => {
                // Remove all filters in progress if apply wasn't pressed
                dispatch({
                  type: ActionTypes.filters.removeInProgress,
                });
                goBack();
              }}
              color={Colors.GrayDark}
            >
              Back to&nbsp;
              {location?.state?.title || 'Discover'}
            </Link>
          </LinkWrapper>

          <HeadlineWrapper>
            <Headline
              variant="page"
              title={{
                text: 'Filter talent',
                color: Colors.GrayVeryDark,
              }}
            />
          </HeadlineWrapper>
          <Form
            formID="filter-talent"
            headingPaddingBottom="0px"
            autocomplete="off"
          >
            <InputWrapper>
              <InputAutocomplete
                label="Graduation season"
                width="auto"
                showOptionsOnFocus
                onChange={(e) => {
                  setSeasonText(e?.target?.value);
                  setSeasonOptions(GRAD_SEASONS.filter((item) => {
                    if (!(filterService?.graduationSeason ?? []).some((filter) => filter !== item?.text)) return new RegExp(seasonText, 'gi').test(item?.text);
                    return null;
                  }));
                }}
                options={removeDuplicated(seasonOptions, 'graduationSeason')}
                onClick={(e, option) => {
                  dispatch({
                    type: ActionTypes.filters.add,
                    filters: {
                      fieldId: 'graduationSeason',
                      content: option?.text,
                    },
                  });
                }}
                selected={getArrayItems(filterService?.graduationSeason)}
                onSelectedClick={(e, option) => {
                  dispatch({
                    type: ActionTypes.filters.remove,
                    filters: {
                      fieldId: 'graduationSeason',
                      content: option?.text,
                    },
                  });
                }}
              />
            </InputWrapper>
            <InputWrapper>
              <InputAutocomplete
                label="Graduation year"
                width="auto"
                showOptionsOnFocus
                {...!(searchService?.graduationYear?.results ?? []).length && {
                  onFocus: () => onSearch('graduationYear', '2'),
                }}
                isLoading={searchService?.graduationYear?.fetching}
                onChange={(e) => onSearch('graduationYear', e?.target?.value)}
                options={removeDuplicated(getArrayItems(searchService?.graduationYear?.results), 'graduationYear')}
                onClick={(e, option) => {
                  dispatch({
                    type: ActionTypes.filters.add,
                    filters: {
                      fieldId: 'graduationYear',
                      content: option?.text,
                    },
                  });
                }}
                selected={getArrayItems(filterService?.graduationYear)}
                onSelectedClick={(e, option) => {
                  dispatch({
                    type: ActionTypes.filters.remove,
                    filters: {
                      fieldId: 'graduationYear',
                      content: option?.text,
                    },
                  });
                }}
              />
            </InputWrapper>
            <InputWrapper>
              <InputAutocomplete
                label="Location interests"
                width="auto"
                showOptionsOnFocus
                {...!(searchService?.location?.results ?? []).length && {
                  onFocus: () => onSearch('location', ''),
                }}
                onFocus={handleLocationsFocus}
                isLoading={searchService?.location?.fetching}
                isPaginating={searchService?.location?.paginating}
                onScroll={locationsScroll}
                onChange={(e) => onSearch('location', e?.target?.value)}
                options={
                  getArrayItems(searchService?.location?.results, selectedLocations)
                }
                onClick={(e, option) => {
                  const newLocations = [...selectedLocations, option];
                  setLocations(newLocations);
                  dispatch({
                    type: ActionTypes.filters.add,
                    filters: {
                      fieldId: 'location',
                      content: { _id: option?._id, name: option?.text },
                    },
                  });
                }}
                selected={selectedLocations}
                onSelectedClick={(e, option) => {
                  const newLocations = selectedLocations.filter(
                    (item) => item?._id !== option?._id,
                  );
                  setLocations(newLocations);
                  dispatch({
                    type: ActionTypes.filters.remove,
                    filters: {
                      fieldId: 'location',
                      content: option?.text,
                    },
                  });
                }}
              />
            </InputWrapper>
            <InputWrapper>
              <InputAutocomplete
                label="Career interests"
                width="auto"
                showOptionsOnFocus
                {...!(searchService?.interests?.results ?? []).length && {
                  onFocus: () => onSearch('interests', ''),
                }}
                onFocus={handleInterestsFocus}
                isLoading={searchService?.interests?.fetching}
                isPaginating={searchService?.interests?.paginating}
                onScroll={interestsScroll}
                onChange={(e) => onSearch('interests', e?.target?.value)}
                options={
                  getArrayItems(searchService?.interests?.results, selectedInterests)
                }
                onClick={(e, option) => {
                  const newInterests = [...selectedInterests, option];
                  setInterests(newInterests);
                  dispatch({
                    type: ActionTypes.filters.add,
                    filters: {
                      fieldId: 'interests',
                      content: { _id: option?._id, name: option?.text },
                    },
                  });
                }}
                selected={selectedInterests}
                onSelectedClick={(e, option) => {
                  const newInterests = selectedInterests.filter(
                    (item) => item?._id !== option?._id,
                  );
                  setInterests(newInterests);
                  dispatch({
                    type: ActionTypes.filters.remove,
                    filters: {
                      fieldId: 'interests',
                      content: option?.text,
                    },
                  });
                }}
              />
            </InputWrapper>
            <InputWrapper lastChild>
              <InputAutocomplete
                label="College or league"
                width="auto"
                showOptionsOnFocus
                {...!(searchService?.collegeOrLeague?.results ?? []).length && {
                  onFocus: () => onSearch('collegeOrLeague', ''),
                }}
                onFocus={handleCollegeLeague}
                isLoading={searchService?.collegeOrLeague?.fetching}
                isPaginating={searchService?.collegeOrLeague?.paginating}
                onScroll={collegeOrLeagueScroll}
                onChange={(e) => onSearch('collegeOrLeague', e?.target?.value)}
                options={
                  getArrayItems(searchService?.collegeOrLeague?.results, selectedCollegeLeagues)
                }
                onClick={(e, option) => {
                  const newCollegeLeague = [...selectedCollegeLeagues, option];
                  setCollegeLeagues(newCollegeLeague);
                  dispatch({
                    type: ActionTypes.filters.add,
                    filters: {
                      fieldId: 'collegeOrLeague',
                      content: { _id: option?._id, name: option?.text },
                    },
                  });
                }}
                selected={selectedCollegeLeagues}
                onSelectedClick={(e, option) => {
                  const newCollegeLeague = selectedCollegeLeagues.filter(
                    (item) => item?._id !== option?._id,
                  );
                  setCollegeLeagues(newCollegeLeague);
                  dispatch({
                    type: ActionTypes.filters.remove,
                    filters: {
                      fieldId: 'collegeOrLeague',
                      content: option?.text,
                    },
                  });
                }}
              />
            </InputWrapper>

            <ButtonGroup isNonDesktop={viewport === 'mobileSmall'}>
              <StyledButton
                variant="primary"
                width="100%"
                isMobile={viewport === 'mobile'}
                isMobileSmall={viewport === 'mobileSmall'}
                isLoading={discoverService?.fetching}
                disabled={discoverService?.fetching}
                onClick={() => {
                  dispatch({
                    type: ActionTypes.discover.postFilters.START,
                    filters: {
                      graduationSeason: filterService?.graduationSeason || [],
                      graduationYear: filterService?.graduationYear ?? [],
                      location: (filterService?.location || []).map((filter) => filter?._id),
                      interests: (filterService?.interests || []).map((filter) => filter?._id),
                      collegeOrLeague:
                      (filterService?.collegeOrLeague || []).map((filter) => filter?._id),
                    },
                  });
                  dispatch({
                    type: ActionTypes.analytics.logEvent.START,
                    name: eventNames.applied_filters,
                  });
                }}
              >
                Apply
              </StyledButton>
              <Button
                variant="primary"
                width="100%"
                inverted
                disabled={discoverService?.fetching}
                onClick={() => {
                  if (!Object.keys(filterService).length) {
                    goBack();
                  } else {
                    dispatch({
                      type: ActionTypes.filters.removeAll,
                    });
                    goBack();
                  }
                  dispatch({
                    type: ActionTypes.analytics.logEvent.START,
                    name: eventNames.remove_filters,
                  });
                }}
              >
                Reset
              </Button>

            </ButtonGroup>
          </Form>
        </PageWrapper>

      </ContentContainer>

    </>
  );
};

export default (withAppManager(FilterScreen): React$ComponentType<ManagedProps<Props>>);
